import React, {
    useEffect,
    useCallback,
} from 'react';
import {batch} from 'react-redux';
import {curry} from 'ramda';
import wrap from 'wraptile';
import getSelection from '@pluto/get-selection';
import {ApTimelineMetadata} from '@accurate-player/accurate-player-components-external';
import {ifApTimelineMetadata} from '@pluto/player-check/player-check';

import {Item} from '../../QCMedia/Redux/QCMediaActions';
import {TabIndex} from '../../QCTabs/QCTabsTypes';
import {SetActiveTab} from '../../QCTabs/Redux/QCTabsActions';
import {
    TimelineRow,
    TimelineMarker,
} from '../TimelineTypes';
import AdBreaks from './AdBreaks';
import {ToggleSelectedMarker, ToggleSelectedRow} from '../Redux/TimelineActions';
import {
    SetAdBreaksAction,
    ResetAdBreaksAction,
} from './Redux/AdBreaksActions';

export interface AdBreaksContainerProps {
    url: string;
    item: Item;
    markerRowData: TimelineRow;
    setMarkers: SetAdBreaksAction;
    resetMarkers: ResetAdBreaksAction;
    selectedRowId: string;
    selectedMarkerId: string;
    toggleSelectedMarker: ToggleSelectedMarker;
    toggleSelectedRow: ToggleSelectedRow;
    setActiveTab: SetActiveTab;
}

export const onMarkerRowClicked = wrap((markerRowData: TimelineRow, toggleSelectedRow: ToggleSelectedRow) => {
    const {
        identifier,
        type,
    } = markerRowData;
    
    toggleSelectedRow(identifier, type);
});

export const updateMarkerRowContainer = ifApTimelineMetadata((props: AdBreaksContainerProps, node: ApTimelineMetadata) => {
    const {
        markerRowData,
        selectedMarkerId,
        selectedRowId,
    } = props;
    
    node.style.setProperty('--ap-timeline-metadata-overflow', 'visible');
    node.selection = getSelection(markerRowData, selectedMarkerId, selectedRowId);
});

export const handleMarkerRowContainerChange = curry((props: AdBreaksContainerProps, node: ApTimelineMetadata) => {
    updateMarkerRowContainer(node, props, node);
});

const useMarkerRowContainerRef = (props: AdBreaksContainerProps) => {
    const onMarkerChange = handleMarkerRowContainerChange(props);
    const setRef = useCallback(onMarkerChange, [props]);
    
    return [setRef];
};

export const handleMount = wrap((props: AdBreaksContainerProps) => {
    const {
        url,
        item,
        setMarkers,
        resetMarkers,
    } = props;
    const {duration} = item.video;
    
    setMarkers(url, 0, duration);
    
    return resetMarkers;
});

export const dispatchClickActions = wrap((props: AdBreaksContainerProps, marker: TimelineMarker) => {
    const {
        markerRowData,
        toggleSelectedMarker,
        setActiveTab,
        selectedMarkerId,
    } = props;
    const {
        identifier,
        type,
    } = markerRowData;
    
    // Do not toggle tab when deselecting Ad break
    if (selectedMarkerId !== marker.identifier)
        setActiveTab(TabIndex.AdBreaks);
    
    toggleSelectedMarker(marker.identifier, identifier, type);
});

export const onDiamondClicked = wrap((props: AdBreaksContainerProps, marker: TimelineMarker) => {
    batch(dispatchClickActions(props, marker));
});

export default function AdBreaksContainer(props: AdBreaksContainerProps) {
    const {
        markerRowData,
        toggleSelectedRow,
        selectedMarkerId,
        selectedRowId,
    } = props;
    const {
        markers,
        label,
        identifier,
    } = markerRowData;
    const [setMarkerRowContainerRef] = useMarkerRowContainerRef(props);
    const onMount = handleMount(props);
    
    useEffect(onMount, []);
    const handleMarkerRowClicked = onMarkerRowClicked(markerRowData, toggleSelectedRow);
    
    const handleDiamondClicked = onDiamondClicked(props);
    
    return (
        <AdBreaks
            setMarkerRowContainerRef={setMarkerRowContainerRef}
            onRowClicked={handleMarkerRowClicked}
            rowLabel={label}
            rowId={identifier}
            markers={markers}
            selectedRowId={selectedRowId}
            selectedMarkerId={selectedMarkerId}
            onDiamondClicked={handleDiamondClicked}
        />
    );
}
